import { Grid, Box, Container, Typography } from "@mui/material";
import { useFormik } from "formik";
import { setLocalStorageData } from "../../../../shared/utils/localStorage";
import { LoadingButton } from "@mui/lab";
import InputTextfield from "../../../../shared/components/textField";
import { useContext, useEffect, useRef, useState } from "react";
import { SnackBarAlertContext } from "../../../../contexts/SnackbarContext";
import { useNavigate } from "react-router-dom";
import LoginPageCard from "../../../../shared/components/auth/loginPageCard";
import MenuContainer from "../../../../shared/components/auth/menuContainer";
import { otpValidationSchema, phoneValidationSchema } from "./customSchema";

import * as Yup from "yup";
import { NavLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import LoginBG from "../../../../assets/img/login-svg.svg";
import LoginLine from "../../../../assets/img/login-line.svg";
import OTPInput from "react-otp-input";

const StyledMenuBg = styled(Box)(
  ({ theme }) => `
    top: 0;
    background: transparent;
    padding: 0 10%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: start !important;

    img {
      max-width: 100%;
      height: auto;
      display: block;
    }
  `
);

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number>(3);
  const snackBarAlertContext = useContext(SnackBarAlertContext);

  const [timer, setTimer] = useState(30); // 30 seconds
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setTimeout(() => setTimer((prev) => prev - 1), 1000);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timer]);


  // Phone form
  const phoneForm = useFormik({
    initialValues: {
      phone: "",
    },
    validationSchema: phoneValidationSchema,
    onSubmit: async (loginData: any) => {
      setLoading(true);
      try {
        const response = await fetch(
          process.env.REACT_APP_API_URL + "member/login",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(loginData),
          }
        );

        const responseData = await response.json();

        if (response.ok) {
          setLoading(false);
          setIsOtpSent(true);
          setTimer(60);

          snackBarAlertContext.set({
            status: true,
            type: "success",
            message:
              responseData.message || "OTP has been sent to your phone number",
          });

          otpForm.setValues({
            phone: loginData.phone,
            otp: "",
          });
        } else {
          setLoading(false);
          snackBarAlertContext.set({
            status: true,
            type: "error",
            message: responseData?.message || "Failed to send OTP",
          });
        }
      } catch (error) {
        setLoading(false);
        snackBarAlertContext.set({
          status: true,
          type: "error",
          message: "An error occurred. Please try again.",
        });
      }
    },
  });

  // OTP form
  const otpForm = useFormik({
    initialValues: {
      phone: phoneForm.values.phone,
      otp: "",
    },
    validationSchema: otpValidationSchema,
    onSubmit: async (otpData: any) => {
      setLoading(true);
      try {
        const response = await fetch(
          process.env.REACT_APP_API_URL + "member/otpVerify",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(otpData),
          }
        );

        if (response.ok) {
          setLoading(false);
          const token = response.headers.get("token");
          setLocalStorageData("token", token);
          const responseData = await response.json();
          const { parent } = responseData?.data || {};
          // setLocalStorageData("user-name", parent?.email);
          setLocalStorageData("name", parent?.name);
          navigate("/dashboard");
          snackBarAlertContext.set({
            status: true,
            type: "success",
            message: "Login successful!",
          });
        } else {
          const error = await response.json();
          setLoading(false);

          if (
            error.additionalData &&
            error.additionalData["remaining attempts"] !== undefined
          ) {
            setAttemptsRemaining(error.additionalData["remaining attempts"]);
          }

          snackBarAlertContext.set({
            status: true,
            type: "error",
            message: error?.message || "Invalid OTP",
          });
        }
      } catch (error) {
        setLoading(false);
        snackBarAlertContext.set({
          status: true,
          type: "error",
          message: "An error occurred. Please try again.",
        });
      }
    },
  });

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (attemptsRemaining <= 0) {
      snackBarAlertContext.set({
        status: true,
        type: "error",
        message: "OTP resend limit exceeded",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "member/resend-otp",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phone: phoneForm.values.phone }),
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        setLoading(false);
        setTimer(60);
        if (
          responseData.data &&
          responseData.data.attemptsRemaining !== undefined
        ) {
          setAttemptsRemaining(responseData.data.attemptsRemaining);
        } else {
          setAttemptsRemaining((prev) => Math.max(0, prev - 1));
        }

        snackBarAlertContext.set({
          status: true,
          type: "success",
          message:
            responseData.message || "OTP has been resent to your phone number",
        });
      } else {
        setLoading(false);
        snackBarAlertContext.set({
          status: true,
          type: "error",
          message: responseData?.message || "Failed to resend OTP",
        });
      }
    } catch (error) {
      setLoading(false);
      snackBarAlertContext.set({
        status: true,
        type: "error",
        message: "An error occurred. Please try again.",
      });
    }
  };

  // Go back to phone input
  const handleBackToPhone = () => {
    setIsOtpSent(false);
  };

  return (
    <>
      <Box sx={{ height: "100%", width: "100%" }}>
        <Container>
          <Grid
            container
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Grid item md={3}>
              <MenuContainer />
              <StyledMenuBg>
                <img src={LoginBG} alt="Logo" />
              </StyledMenuBg>{" "}
            </Grid>
            <Grid item md={0.75}>
              <img style={{ padding: "0 12px" }} src={LoginLine} alt="Logo" />
            </Grid>
            <Grid item md={2.5}>
              {!isOtpSent ? (
                // Phone Number Form
                <form
                  onSubmit={phoneForm.handleSubmit}
                  autoComplete="off"
                  className="loginForm"
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Enter your phone number
                  </Typography>
                  <Grid sx={{ mt: 1 }}>
                    <InputTextfield
                      form={phoneForm}
                      fieldProps={{
                        id: "login-phone",
                        name: "phone",
                        placeholder: "Enter your 10-digit phone number",
                        type: "text",
                        required: true,
                        label: "Phone Number",
                      }}
                    />
                  </Grid>
                  <Grid sx={{ mt: 3 }} container spacing={1}>
                    <Grid item md={12}>
                      <LoadingButton
                        loading={loading}
                        variant="contained"
                        type="submit"
                        fullWidth
                        size="medium"
                        aria-label="Click here to get OTP"
                      >
                        Get OTP
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                // OTP Verification Form
                <form
                  onSubmit={otpForm.handleSubmit}
                  autoComplete="off"
                  className="otpForm"
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Verify OTP
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Enter the OTP sent to {phoneForm.values.phone}
                  </Typography>
                  <Grid sx={{ mt: 1 }}>
                    <OTPInput
                      value={otpForm.values.otp}
                      onChange={(otp) => otpForm.setFieldValue("otp", otp)}
                      numInputs={4}
                      renderInput={(props) => (
                        <input {...props} className="otp-input" />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ mt: 3 }} container spacing={1}>
                    <Grid item md={12}>
                      <LoadingButton
                        loading={loading}
                        variant="contained"
                        type="submit"
                        fullWidth
                        size="medium"
                        aria-label="Click here to verify OTP"
                      >
                        Verify OTP
                      </LoadingButton>
                    </Grid>
                    <Grid
                      item
                      md={12}
                      mt={2}
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: "pointer",
                          color: "primary.main",
                          textDecoration: "underline",
                        }}
                        onClick={handleBackToPhone}
                      >
                        Back
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: "underline",
                          cursor: timer > 0 ? "not-allowed" : "pointer",
                          color: timer > 0 ? "text.disabled" : "primary.main",
                        }}
                        onClick={() => {
                          if (timer <= 0) {
                            handleResendOTP();
                            setTimer(30); // restart timer
                          }
                        }}
                      >
                        {timer > 0 ? `Resend OTP in  ${timer}s` : "Resend OTP"}
                      </Typography>

                    </Grid>
                  </Grid>
                </form>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Login;
